/**
 * Self-serve catalog (owner decision 2026-09-15, SaaS launch Phase 4d):
 * two tiers × two billing intervals. Amounts and the 14-day trial live on the
 * Paddle prices (backend env PADDLE_PRICE_<TIER>_<INTERVAL>); everything here is
 * FALLBACK display copy — the pricing page (lib/pricing.ts → GET /api/saas/pricing)
 * and the checkout step (checkout/context) read the live amounts from Paddle
 * through the backend, so a price change is made in Paddle, not in code. These
 * numbers only show when the backend is unreachable at render time.
 */
export type Tier = "solo" | "team";
export type Interval = "monthly" | "yearly";
export type PlanId = `${Tier}-${Interval}`;

export type Plan = {
  id: PlanId;
  tier: Tier;
  billing: Interval;
  name: string;
  /** Display fallback (the live amount comes from Paddle). */
  price: string;
  priceSub: string;
  /** Yearly plans: the total billed once per year. */
  yearlyTotal?: string;
  badge?: string;
};

export const TRIAL_DAYS = 14;

export const TIERS: Tier[] = ["solo", "team"];
export const INTERVALS: Interval[] = ["monthly", "yearly"];

export const planId = (tier: Tier, interval: Interval): PlanId => `${tier}-${interval}`;

/** "solo-yearly" → { tier, interval }; legacy "monthly"/"yearly"/"standard-monthly" → Team. */
export function parsePlanId(plan?: string | null): { tier: Tier; interval: Interval } {
  const p = (plan || "").toLowerCase();
  const tier: Tier = p.startsWith("solo") ? "solo" : "team";
  const interval: Interval = /year/.test(p) ? "yearly" : "monthly";
  return { tier, interval };
}

/** Numeric fallback (EUR) behind `plans` — used when the live catalog is unavailable. */
export const FALLBACK_AMOUNTS: Record<Tier, Record<Interval, { perMonth: number; total: number }>> = {
  solo: { monthly: { perMonth: 19, total: 19 }, yearly: { perMonth: 16, total: 190 } },
  team: { monthly: { perMonth: 69, total: 69 }, yearly: { perMonth: 58, total: 690 } },
};

export const plans: Plan[] = [
  { id: "solo-monthly", tier: "solo", billing: "monthly", name: "Solo", price: "€19", priceSub: "/ month" },
  { id: "solo-yearly", tier: "solo", billing: "yearly", name: "Solo", price: "€16", priceSub: "/ month", yearlyTotal: "€190" },
  { id: "team-monthly", tier: "team", billing: "monthly", name: "Team", price: "€69", priceSub: "/ seat / month", badge: "Most popular" },
  { id: "team-yearly", tier: "team", billing: "yearly", name: "Team", price: "€58", priceSub: "/ seat / month", yearlyTotal: "€690", badge: "Most popular" },
];

export const planFor = (tier: Tier, interval: Interval): Plan =>
  plans.find((p) => p.tier === tier && p.billing === interval) ?? plans[2];

/**
 * Short plan-limit bullets for compact surfaces (checkout step). The long
 * marketing bullets live on the pricing section; the numbers must match
 * `constants/fair-use.ts` and the backend plan config.
 */
export const PLAN_SHORT_BULLETS: Record<"en" | "de", Record<Tier, string[]>> = {
  en: {
    solo: ["1 user", "Up to 10,000 leads", "3 active workflows", "Last 7 days of email on connect"],
    team: ["Unlimited users, roles & team management", "Up to 1,000,000 leads", "Unlimited workflows", "12 months of email on connect"],
  },
  de: {
    solo: ["1 Nutzer", "Bis zu 10.000 Leads", "3 aktive Workflows", "E-Mails der letzten 7 Tage beim Verbinden"],
    team: ["Unbegrenzt Nutzer, Rollen & Team-Verwaltung", "Bis zu 1.000.000 Leads", "Unbegrenzt Workflows", "12 Monate E-Mail-Verlauf beim Verbinden"],
  },
};
